/** Firestore collection paths in one place, so a rename is a single edit and
 * the rules file, functions and apps can never drift apart. */
export const COL = {
    categories: 'categories',
    products: 'products',
    media: 'media',
    carts: 'carts',
    orders: 'orders',
    artworkFiles: 'artworkFiles',
    vendors: 'vendors',
    /**
     * Redacted per-vendor view of one order line. Firestore has no field-level
     * read control, so vendors never read `orders` directly — they would see
     * customer pricing. Functions project the safe subset into here instead.
     */
    vendorJobs: 'vendorJobs',
    vendorSettlements: 'vendorSettlements',
    designs: 'designs',
    notifications: 'notifications',
    couponRedemptions: 'couponRedemptions',
    users: 'users',
    addresses: 'addresses',
    /** Per-user saved studio designs, kept for reordering. */
    savedJobs: 'savedJobs',
    /**
     * Gang-run press sheets laid out in the print lab. Top-level and shop-wide:
     * the print lab is a pre-press tool for staff and vendors, so a sheet belongs
     * to the shop, not to whichever staff member laid it out. (It used to live at
     * users/{uid}/savedJobs, back when customers imposed their own sheets.)
     */
    impositionJobs: 'impositionJobs',
    /** CRM contacts/leads (admin). Interactions live in an `interactions` sub. */
    crmContacts: 'crmContacts',
    /** Shoppable product reels shown on the storefront home. */
    reels: 'reels',
    /** Marketing banners shown in the storefront home hero. */
    banners: 'banners',
    /** Customer product reviews. */
    reviews: 'reviews',
    /* -------- artist marketplace -------- *
     * Designers mirror vendors: an outside party paid periodically who must see
     * a redacted slice of orders and never `orders` itself. */
    designers: 'designers',
    artworks: 'artworks',
    /** Redacted per-designer projection of an order line's royalty. Artists read
     *  this, never `orders` — Firestore has no field-level read control. */
    artistInvites: 'artistInvites',
    artistReferrals: 'artistReferrals',
    artworkViewsDaily: 'artworkViewsDaily',
    artworkLikes: 'artworkLikes',
    artworkStats: 'artworkStats',
    designerEarnings: 'designerEarnings',
    referralDaily: 'referralDaily',
    referralVisits: 'referralVisits',
    designerSettlements: 'designerSettlements',
    /** Commissioned design work. Private to the customer who paid and the
     *  designer doing it — never a public listing, unlike `artworks`. The
     *  conversation lives in a `messages` subcollection. */
    commissions: 'commissions',
    /** Ratings of PEOPLE — designers and customers — each tied to a real
     *  transaction. Distinct from `reviews`, which rates a product and is
     *  postable by anyone signed in. */
    ratings: 'ratings',
    /** A customer's aggregate rating. Its own collection rather than a field on
     *  a user document, because designers must be able to read the aggregate
     *  without being able to read anything else about the person. */
    customerReputation: 'customerReputation',
    /** Rights complaints against a listing. Open upload means a report is the
     *  first review most artwork gets, so this is the audit trail a dispute is
     *  decided on. */
    artworkReports: 'artworkReports',
    /** People asking to become artists, with three samples each. Kept apart from
     *  `designers` so nothing exists under a handle until it is approved. */
    artistApplications: 'artistApplications',
    /** Who minted each application upload: {artworkId} -> {uid}. Server-only.
     *  An application may only name artwork ids its own account minted - before
     *  this it could name anybody's published piece, and rejecting it deleted
     *  that piece while approving it reassigned it (22 Oct 2026 review). */
    artistUploads: 'artistUploads',
    coupons: 'coupons',
    blogPosts: 'blogPosts',
    payments: 'payments',
    /** Every webhook delivery, raw, keyed on the provider's own event id —
     *  Razorpay's `x-razorpay-event-id`, Postmark's `MessageID`. Idempotency and
     *  an audit trail in one: it is the only way to work out afterwards what a
     *  provider actually told us, and when. */
    webhookEvents: 'webhookEvents',
    /** Email replies that could not be put on an order: the address did not
     *  verify, or the sender was not the customer. PARKED, never dropped — a
     *  customer replying from their work address is the ordinary case, and
     *  binning them silently would be worse than the problem inbound email
     *  solves. Staff admit them to the thread from the admin. */
    inboundRejected: 'inboundRejected',
    /** Chargebacks. Nothing here until one happens, which is the point — a
     *  dispute used to be invisible until the money had already gone. */
    disputes: 'disputes',
    /** One document per Razorpay settlement, with the rows it paid out and an
     *  `unmatched` subcollection for anything the report could not explain. */
    settlements: 'settlements',
    invoices: 'invoices',
    counters: 'counters',
    auditLog: 'auditLog',
    /**
     * One row per visit that came from an AI assistant, or from an LLM crawler.
     * Written server-side only; no personal data, only the assistant name and
     * the path landed on. This is how we find out whether the llms.txt and
     * structured-data work actually produces referrals.
     */
    llmVisits: 'llmVisits',
    /** Rolled-up daily counts, so the dashboard never scans the raw rows. */
    llmVisitDaily: 'llmVisitDaily',
    /** Per-user rolling-hour quota for the AI design assistant. */
    usage: 'aiUsage',
    /** One row per model call — uid, tokens, outcome — for cost monitoring. */
    aiUsageLog: 'aiUsageLog',
    /** AI artwork enhancements paid for, per order line (`${orderId}_${itemId}`). */
    artworkEnhanceCounts: 'artworkEnhanceCounts',
    /**
     * Print Credits wallet, one per signed-in customer, keyed by auth uid.
     * Owner-readable; written only by Cloud Functions — a balance is money.
     */
    loyaltyAccounts: 'loyaltyAccounts',
    /**
     * The loyalty programme's rules, including the per-tier earn rate. A single
     * `default` doc. Admin + functions only, NEVER world-readable — this is where
     * the earn percentage stays out of the customer's reach.
     */
    loyaltyConfig: 'loyaltyConfig',
    /**
     * World-readable projection of the loyalty programme — tier names, thresholds
     * and perks, but NEVER the earn rate. Written by admin alongside the private
     * config; read by the storefront to describe the programme.
     */
    loyaltyProgram: 'loyaltyProgram',
    /**
     * Shop-wide operational settings. A single `fulfilment` doc holds
     * `defaultVendorId` — who produces the work when nobody has said otherwise,
     * normally this shop's own in-house vendor record. Admin + functions only.
     */
    settings: 'settings',
};
/** Subcollections. */
export const SUB = {
    optionGroups: 'optionGroups',
    optionValues: 'values',
    priceTiers: 'priceTiers',
};
export const optionGroupsPath = (productId) => `${COL.products}/${productId}/${SUB.optionGroups}`;
export const optionValuesPath = (productId, groupId) => `${COL.products}/${productId}/${SUB.optionGroups}/${groupId}/${SUB.optionValues}`;
export const priceTiersPath = (productId) => `${COL.products}/${productId}/${SUB.priceTiers}`;
/** Append-only Print Credits ledger for one customer (lots, spends, expiries). */
export const loyaltyLedgerPath = (uid) => `${COL.loyaltyAccounts}/${uid}/ledger`;
/** The single loyalty config document id, and its public projection's id. */
export const LOYALTY_CONFIG_ID = 'default';
export const LOYALTY_PROGRAM_ID = 'public';
/** Storage layout. Artwork is never publicly readable — vendors get signed URLs. */
export const STORAGE = {
    media: (filename) => `media/${filename}`,
    mediaVariant: (mediaId, variant, ext) => `media/variants/${mediaId}/${variant}.${ext}`,
    /**
     * A line's artwork. With a placement, one directory per decorated position.
     *
     * The position is a path SEGMENT rather than a filename prefix so the upload
     * trigger can read it back without parsing a name a customer chose, and so two
     * positions cannot collide on the same file name.
     */
    artwork: (orderId, itemId, filename, placementId) => placementId
        ? `orders/${orderId}/artwork/${itemId}/${placementId}/${filename}`
        : `orders/${orderId}/artwork/${itemId}/${filename}`,
    artworkPreview: (orderId, artworkFileId) => `orders/${orderId}/previews/${artworkFileId}.png`,
    vendorProof: (orderId, vendorId, filename) => `orders/${orderId}/proofs/${vendorId}/${filename}`,
};
//# sourceMappingURL=collections.js.map